import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { usePublicConfig } from "../lib/publicConfig";

const DISMISSED_KEY = "fc_dismissed_banners";

// Phase 11 fix — key dismissed flags by a content fingerprint, not just the
// banner id, so an admin edit (new message / cta / countdown) auto re-shows
// the banner for everyone who dismissed the prior version.
function fingerprint(b) {
  return `${b.id}::${b.message || ""}::${b.cta_url || ""}::${b.cta_text || ""}::${b.countdown_to || ""}`;
}

function getDismissed() {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]")); } catch { return new Set(); }
}
function markDismissed(id) {
  const cur = getDismissed();
  cur.add(id);
  localStorage.setItem(DISMISSED_KEY, JSON.stringify([...cur]));
}

function formatRemaining(ts) {
  if (!ts) return null;
  const target = new Date(ts).getTime();
  const now = Date.now();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

export function PromoBanner({ location = "global" }) {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(getDismissed());
  // Phase 13.1 — promo banners are gated by the `payments_enabled` feature
  // flag. While we're in complimentary-early-access mode, no commercial
  // banners (pricing, tiers, "% off", "upgrade") should appear, even if
  // legacy `is_active=true` rows exist in the DB. When admins flip payments
  // back on the existing per-row toggle resumes.
  const { payments_enabled } = usePublicConfig();

  useEffect(() => {
    if (!payments_enabled) {
      setBanners([]);
      return;
    }
    api.get(`/promo-banners?location=${location}`).then(r => setBanners(r.data.banners || [])).catch(() => {});
  }, [location, payments_enabled]);

  // Tick for countdown
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const visible = banners.filter(b => !dismissed.has(fingerprint(b)));
  if (!visible.length) return null;
  const b = visible[0];
  const remaining = formatRemaining(b.countdown_to);

  const dismiss = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    markDismissed(fingerprint(b));
    setDismissed(getDismissed());
  };

  return (
    <div className="bg-fc-red text-white px-4 py-2 flex items-center justify-center gap-3 text-xs font-bold tracking-wider" data-testid={`promo-banner-${b.id}`}>
      <span>{b.message}</span>
      {remaining && <span className="font-mono bg-black/20 px-2 py-0.5">{remaining}</span>}
      {b.cta_url && <a href={b.cta_url} className="underline hover:text-gray-900" data-testid={`promo-cta-${b.id}`}>{b.cta_text || "Learn more"}</a>}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss banner"
        title="Dismiss banner"
        className="ml-2 inline-flex items-center justify-center w-8 h-8 -mr-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-gray-200 active:bg-white/25 transition-colors"
        data-testid={`promo-dismiss-${b.id}`}
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function SponsorBanner({ location = "homepage" }) {
  const [placements, setPlacements] = useState([]);
  useEffect(() => {
    api.get(`/sponsor-placements?location=${location}`).then(r => {
      setPlacements(r.data.placements || []);
      // Track impressions
      for (const p of (r.data.placements || [])) {
        api.post("/sponsor-events/impression", { placement_id: p.id }).catch(() => {});
      }
    }).catch(() => {});
  }, [location]);

  if (!placements.length) return null;

  return (
    <div className="px-4 lg:px-10 py-3 bg-white border-y border-gray-200 flex flex-wrap gap-3 items-center justify-center" data-testid={`sponsor-banner-${location}`}>
      <span className="text-[10px] tracking-[0.22em] uppercase text-gray-500 font-bold">SPONSORED</span>
      {placements.map(p => (
        <a
          key={p.id}
          href={p.cta_url || "#"}
          target="_blank"
          rel="noreferrer"
          onClick={() => api.post("/sponsor-events/click", { placement_id: p.id }).catch(() => {})}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          data-testid={`sponsor-${p.id}`}
        >
          {p.type === "banner" ? (
            <img src={p.creative_url} alt={p.sponsor?.name} className="h-8 w-auto max-w-[180px] object-contain" />
          ) : (
            <video src={p.creative_url} className="h-12 w-auto" muted loop autoPlay />
          )}
          {p.cta_text && <span className="text-xs font-bold tracking-wider text-fc-red">{p.cta_text} →</span>}
        </a>
      ))}
    </div>
  );
}
